// Task card component for today's mission
// CREATED: Displays summary of an assigned task with status and progress
// MODIFIED: Simplified mobile-first design showing only name, time, and duration
// MODIFIED: Added checkbox/completed indicators, removed status colors, time/duration as subtitle
// MODIFIED: Moved status indicator to right side, vertically centered
// UPDATED: Status values changed to not_started, completed, overdue
// UPDATED: Completed tasks have grayed background and strikethrough title
// UPDATED: Added temporal access indicators (past/future tasks) with visual badges
// REFACTORED: Using centralized formatTimeToHHMM from dateUtils
// UPDATED: UX optimization - removed redundant lock icon, keeping only visual indicators (gray background + title)

import { Clock, CheckCircle, Square } from 'lucide-react';
import { AssignedTaskWithDetails } from '../../lib/execution';
import { getTaskTemporality } from '../../lib/taskPermissions';
import { formatTimeToHHMM, formatDuration } from '../../lib/dateUtils';

interface TaskCardProps {
  task: AssignedTaskWithDetails;
  onClick: () => void;
}

export function TaskCard({ task, onClick }: TaskCardProps) {
  const estimatedDuration = task.duration_override_minutes || task.template.estimated_duration_minutes;
  const scheduledTime = task.scheduled_time;

  // Determine task temporality
  const taskDate = task.isVirtualOccurrence && task.occurrenceDate
    ? task.occurrenceDate
    : task.assigned_date;
  const temporality = getTaskTemporality(taskDate);
  const isReadOnly = temporality !== 'today';
  const isCompleted = task.status === 'completed';

  return (
    <button
      onClick={onClick}
      className={`w-full border rounded-xl p-4 text-left transition-all duration-200 active:scale-98 shadow-sm hover:shadow-md min-h-[88px] ${
        isCompleted
          ? 'bg-gray-100 border-gray-300'
          : isReadOnly
          ? 'bg-gray-50 border-gray-300'
          : 'bg-white border-gray-300'
      }`}
    >
      <div className="flex items-center gap-3">
        {/* Left side: Task info */}
        <div className="flex-1 min-w-0">
          <h3 className={`text-base font-semibold leading-tight line-clamp-2 mb-1 ${
            isCompleted
              ? 'text-gray-500 line-through'
              : isReadOnly
              ? 'text-gray-600'
              : 'text-gray-900'
          }`}>
            {task.template.name}
          </h3>

          {/* Subtitle: Time and Duration */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            {scheduledTime && (
              <>
                <Clock size={14} className="text-gray-400" />
                <span>{formatTimeToHHMM(scheduledTime)}</span>
              </>
            )}
            {estimatedDuration && (
              <>
                <span>|</span>
                <span>{formatDuration(estimatedDuration)}</span>
              </>
            )}
          </div>
        </div>

        {/* Right side: Status indicator */}
        <div className="flex-shrink-0">
          {isCompleted ? (
            <CheckCircle size={24} className="text-green-600" strokeWidth={2.5} />
          ) : (
            <Square size={24} className="text-gray-600" strokeWidth={2.5} />
          )}
        </div>
      </div>
    </button>
  );
}
